import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Input, MessageBarActions, MessageBarBody, MessageBarTitle, Option, ProgressBar, Textarea } from '@fluentui/react-components';
import { Attach20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawer, GuideDrawerForm } from '../../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { GuideBannerAction } from '../../../../theme/components/GuideButtons';
import { AmountInput, CurrencyChips, DateInput } from '../../../CashFlow/CashFlow.Form.Bits';
import { useInvoicesText } from '../../../CashFlow/Payments/PaymentInvoices/PaymentInvoices.Bits';
import {
    INVOICE_NUMBER_MAX_LENGTH,
    parseOptionalAmount,
    toDateOnly,
    validateInvoiceForm
} from '../../../../functions/CreditorInvoices.Functions';
import {
    buildAcceptAttribute,
    formatFileSize,
    listAllowedExtensions,
    PRACTITIONER_INVOICE_ALLOWED_FILE_TYPES
} from '../../../../functions/AzureFileUpload.Functions';
import { settlementLabel } from '../PractitionerAccountStatement.Functions';
import {
    CreditorInvoice,
    CreditorInvoiceData,
    SettlementInvoiceCandidate
} from '../../../../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice';
import { AzureFileUploadResult } from '../../../../models/Entities/AzureFiles/AzureFileUpload';
import { Settlement } from '../../../../models/Entities/Settlements/Settlement';
import { ErrorType } from '../../../../models/HttpError';

export type InvoiceFormMode = 'upload' | 'edit' | 'settlement';

export interface CreditorInvoiceFormDrawerProps {
    open: boolean;
    mode: InvoiceFormMode;
    practitionerName: string;
    /** Edición: la factura. */
    invoice?: CreditorInvoice;
    /** Registro de una factura de liquidación: el archivo ya subido en la liquidación. */
    candidate?: SettlementInvoiceCandidate;
    /** Monedas sugeridas (las del estado de cuenta). */
    currencies: string[];
    settlements: Settlement[];
    onDismiss: () => void;
    onUpload: (file: File, data: CreditorInvoiceData, onProgress: (percent: number) => void) => Promise<AzureFileUploadResult>;
    onRetryRegister: (path: string, data: CreditorInvoiceData) => Promise<void>;
    onUpdate: (invoiceId: number, data: CreditorInvoiceData) => Promise<unknown>;
    onRegisterSettlement: (candidate: SettlementInvoiceCandidate, data: CreditorInvoiceData) => Promise<unknown>;
    /** Terminó bien: el padre cierra o vuelve a la lista. */
    onDone: () => void;
    /** Texto del botón secundario (por defecto "Cancelar"). */
    secondaryLabel?: string;
}

const NO_SETTLEMENT = '__none';

const errorDetail = (error: ErrorType | undefined): string =>
    [error?.ErrorMessage, ...(error?.Errors ?? [])].filter(Boolean).filter((item, index, list) => list.indexOf(item) === index).join(' ');

const parseDate = (value: string | Date | null | undefined): Date | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const [year, month, day] = value.slice(0, 10).split('-').map(Number);
        return new Date(year, month - 1, day);
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

/**
 * Drawer de la guía para una factura del profesional: subirla (archivo + datos), editar sus datos o
 * registrar una factura de liquidación ya subida. Número, fecha, importe y liquidación son opcionales.
 */
export const CreditorInvoiceFormDrawer = (props: CreditorInvoiceFormDrawerProps) => {
    const tr = useInvoicesText();
    const [t] = useTranslation();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [file, setFile] = useState<File | undefined>(undefined);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [invoiceDate, setInvoiceDate] = useState<Date | undefined>(undefined);
    const [amountText, setAmountText] = useState('');
    const [currencyCode, setCurrencyCode] = useState<string | undefined>(undefined);
    const [settlementId, setSettlementId] = useState<number | undefined>(undefined);
    const [comments, setComments] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState<number | undefined>(undefined);
    const [saveError, setSaveError] = useState<ErrorType | undefined>(undefined);
    /** Se subió pero no se registró: ruta para reintentar sólo el registro. */
    const [unregistered, setUnregistered] = useState<{ path: string; error?: ErrorType } | undefined>(undefined);

    useEffect(() => {
        if (!props.open) return;
        const invoice = props.invoice;
        setFile(undefined);
        setInvoiceNumber(invoice?.invoiceNumber ?? '');
        setInvoiceDate(parseDate(invoice?.invoiceDate));
        setAmountText(invoice?.amount !== undefined && invoice?.amount !== null ? String(invoice.amount) : '');
        setCurrencyCode(invoice?.currencyCode ? invoice.currencyCode.toUpperCase() : props.currencies.length === 1 ? props.currencies[0] : undefined);
        setSettlementId(invoice?.settlementId ?? props.candidate?.settlementId ?? undefined);
        setComments(invoice?.comments ?? '');
        setSubmitted(false);
        setSaving(false);
        setProgress(undefined);
        setSaveError(undefined);
        setUnregistered(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open, props.mode, props.invoice?.id, props.candidate?.path]);

    const errors = validateInvoiceForm({
        file,
        requireFile: props.mode === 'upload' && !unregistered,
        invoiceNumber,
        invoiceDate,
        amountText,
        currencyCode
    });
    const showError = (key: string) => (submitted && errors[key] ? (errors[key].startsWith('azureFileUpload.') ? t(errors[key]) as string : tr(`form.errors.${errors[key]}`)) : undefined);

    const data = (): CreditorInvoiceData => {
        const amount = parseOptionalAmount(amountText);
        const hasAmount = amount !== undefined && !Number.isNaN(amount);
        return {
            invoiceNumber: invoiceNumber.trim() || null,
            invoiceDate: toDateOnly(invoiceDate) ?? null,
            amount: hasAmount ? amount : null,
            currencyCode: currencyCode ?? null,
            settlementId: settlementId ?? null,
            comments: comments.trim() || null
        };
    };

    const run = async (action: () => Promise<void>) => {
        setSaving(true);
        setSaveError(undefined);
        try {
            await action();
        } catch (error: unknown) {
            setSaveError(error as ErrorType);
        } finally {
            setSaving(false);
            setProgress(undefined);
        }
    };

    const submit = () => {
        setSubmitted(true);
        if (Object.keys(errors).length > 0) return;
        if (unregistered) {
            run(async () => {
                await props.onRetryRegister(unregistered.path, data());
                props.onDone();
            });
            return;
        }
        if (props.mode === 'upload' && file) {
            run(async () => {
                setProgress(0);
                const result = await props.onUpload(file, data(), setProgress);
                if (result.registered === false && result.relativePath) {
                    setUnregistered({ path: result.relativePath, error: result.registerError });
                    return;
                }
                props.onDone();
            });
        } else if (props.mode === 'edit' && props.invoice) {
            const invoiceId = props.invoice.id;
            run(async () => {
                await props.onUpdate(invoiceId, data());
                props.onDone();
            });
        } else if (props.mode === 'settlement' && props.candidate) {
            const candidate = props.candidate;
            run(async () => {
                await props.onRegisterSettlement(candidate, data());
                props.onDone();
            });
        }
    };

    const title = props.mode === 'upload' ? tr('form.uploadTitle') : props.mode === 'edit' ? tr('form.editTitle') : tr('form.settlementTitle');
    const primaryLabel = unregistered
        ? tr('form.retryRegister')
        : props.mode === 'upload' ? tr('form.upload') : props.mode === 'edit' ? tr('form.save') : tr('form.register');
    const extensions = listAllowedExtensions(PRACTITIONER_INVOICE_ALLOWED_FILE_TYPES).join(', ');
    const hasPayments = (props.invoice?.payments ?? []).length > 0;
    const settlementOptions = props.settlements;
    const selectedSettlementText = settlementId ? settlementLabel(settlementId, props.settlements) : tr('form.noSettlement');

    return (
        <GuideDrawer
            open={props.open}
            size="medium"
            title={title}
            eyebrow={tr('form.eyebrow', { name: props.practitionerName })}
            onDismiss={props.onDismiss}
            preventDismiss={saving}
            closeLabel={tr('paymentDrawer.close')}
            testId={`invoice-form-${props.mode}`}
            secondaryAction={{ label: props.secondaryLabel ?? tr('form.cancel'), disabled: saving }}
            primaryAction={{
                label: primaryLabel,
                onClick: submit,
                loading: saving,
                disabled: submitted && Object.keys(errors).length > 0,
                testId: 'invoice-form-submit'
            }}
        >
            <GuideDrawerForm testId="invoice-form">
                {saveError && (
                    <GuideMessageBar intent="error" layout="multiline" data-testid="invoice-form-error">
                        <MessageBarBody>
                            <MessageBarTitle>{props.mode === 'upload' && !unregistered ? tr('form.uploadError') : tr('form.saveError')}</MessageBarTitle>
                            {errorDetail(saveError)}
                            {saveError.ErrorCode === 409 && props.mode === 'edit' && <> {tr('form.currencyLocked')}</>}
                        </MessageBarBody>
                    </GuideMessageBar>
                )}
                {unregistered && (
                    <GuideMessageBar intent="warning" layout="multiline" data-testid="invoice-form-unregistered">
                        <MessageBarBody>
                            <MessageBarTitle>{tr('form.unregisteredTitle')}</MessageBarTitle>
                            {tr('form.unregisteredText', { detail: errorDetail(unregistered.error) })}
                        </MessageBarBody>
                        <MessageBarActions>
                            <GuideBannerAction onClick={submit} disabled={saving}>{tr('form.retryRegister')}</GuideBannerAction>
                        </MessageBarActions>
                    </GuideMessageBar>
                )}

                {props.mode === 'upload' && (
                    <GuideField label={tr('form.file')} required error={showError('file')} hint={tr('form.fileHint', { extensions })}>
                        <div className="pinv-file">
                            <input
                                ref={inputRef}
                                className="pinv-file-input"
                                type="file"
                                accept={buildAcceptAttribute(PRACTITIONER_INVOICE_ALLOWED_FILE_TYPES)}
                                data-testid="invoice-file-input"
                                aria-label={tr('form.file')}
                                onChange={(event) => {
                                    setFile(event.target.files?.[0]);
                                    event.target.value = '';
                                }}
                            />
                            <span className="pinv-file__name" data-testid="invoice-file-name">
                                {file ? <>{file.name} <span className="pinv-secondary ichtys-num">{formatFileSize(file.size)}</span></> : <span className="pinv-secondary-cell">{tr('form.noFile')}</span>}
                            </span>
                            <Button appearance="secondary" icon={<Attach20Regular />} disabled={saving || !!unregistered} onClick={() => inputRef.current?.click()}>
                                {file ? tr('form.changeFile') : tr('form.chooseFile')}
                            </Button>
                        </div>
                        {progress !== undefined && (
                            <ProgressBar value={progress / 100} thickness="large" aria-label={tr('form.uploading', { percent: progress })} />
                        )}
                    </GuideField>
                )}
                {props.mode === 'settlement' && props.candidate && (
                    <p className="ichtys-secondary pinv-summary">{tr('form.fromSettlement', { name: props.candidate.fileName })}</p>
                )}

                <GuideFieldGrid>
                    <GuideField label={tr('form.number')} hint={tr('form.numberHint')} error={showError('invoiceNumber')}
                        counter={{ value: invoiceNumber.length, max: INVOICE_NUMBER_MAX_LENGTH }}>
                        <Input value={invoiceNumber} maxLength={INVOICE_NUMBER_MAX_LENGTH} onChange={(_event, value) => setInvoiceNumber(value.value)} />
                    </GuideField>
                    <GuideField label={tr('form.date')}>
                        <DateInput value={invoiceDate} onChange={setInvoiceDate} placeholder={tr('form.date')} />
                    </GuideField>
                </GuideFieldGrid>
                <GuideFieldGrid>
                    <GuideField label={tr('form.amount')} hint={tr('form.amountHint')} error={showError('amount')}>
                        <AmountInput value={amountText} currency={currencyCode} onChange={(text) => setAmountText(text)} />
                    </GuideField>
                    <GuideField label={tr('form.currency')} error={showError('currencyCode')}
                        hint={props.mode === 'edit' && hasPayments ? tr('form.currencyLocked') : undefined}>
                        <CurrencyChips suggested={props.currencies} value={currencyCode} onChange={setCurrencyCode} />
                    </GuideField>
                </GuideFieldGrid>
                <GuideField label={tr('form.settlement')} hint={tr('form.settlementHint')}>
                    <Dropdown
                        aria-label={tr('form.settlement')}
                        value={selectedSettlementText}
                        selectedOptions={[settlementId ? String(settlementId) : NO_SETTLEMENT]}
                        onOptionSelect={(_event, option) => setSettlementId(option.optionValue && option.optionValue !== NO_SETTLEMENT ? Number(option.optionValue) : undefined)}
                    >
                        <Option value={NO_SETTLEMENT} text={tr('form.noSettlement')}>{tr('form.noSettlement')}</Option>
                        {settlementOptions.map((settlement) => (
                            <Option key={settlement.id} value={String(settlement.id)} text={settlementLabel(settlement.id, props.settlements)}>
                                {`LIQ-${settlement.id} · ${settlementLabel(settlement.id, props.settlements)}`}
                            </Option>
                        ))}
                    </Dropdown>
                </GuideField>
                <GuideField label={tr('form.comments')}>
                    <Textarea value={comments} resize="vertical" onChange={(_event, value) => setComments(value.value)} />
                </GuideField>
            </GuideDrawerForm>
        </GuideDrawer>
    );
};

export default CreditorInvoiceFormDrawer;
